import React, { useState } from "react";
import { GridApiPro, GridRowId, GridRowModes, GridRowModesModel } from "@mui/x-data-grid-pro";
import { isNotNullOrEmptyObject } from "src/utils/objectUtils.ts";

type InlineCrudGridEditMode = "cell" | "row";

export function useInlineCrudGridEditing({
	editMode,
	rows,
	gridApiRef,
	getGridApi,
}: {
	editMode: InlineCrudGridEditMode;
	rows: readonly object[];
	gridApiRef: React.RefObject<GridApiPro | null>;
	getGridApi: () => GridApiPro;
}) {
	const [rowUnderEdit, setRowUnderEdit] = useState<GridRowId | undefined>(undefined);
	const [rowToStartEdit, setRowToStartEdit] = useState<GridRowId | undefined>(undefined);
	const [rowModesModel, setRowModesModel] = useState<GridRowModesModel>({});

	function requestStartNewRowEdit(rowId: GridRowId) {
		setRowToStartEdit(rowId);
	}

	function resetEditing() {
		setRowUnderEdit(undefined);
		setRowModesModel({});
	}

	function stopEdit() {
		setRowUnderEdit(undefined);
	}

	function clearDeletedRows(deletedRowIds: GridRowId[]) {
		setRowModesModel((prev) => {
			const next = { ...prev };
			deletedRowIds.forEach((rowId) => {
				delete next[rowId];
			});
			return next;
		});
		if (rowUnderEdit !== undefined && deletedRowIds.includes(rowUnderEdit)) {
			setRowUnderEdit(undefined);
		}
	}

	const startToEditNewRow = React.useCallback((rowId: GridRowId) => {
		const gridApi = getGridApi();

		const firstEditableField = gridApi.getAllColumns().find((column) => column.editable)?.field;
		if (!firstEditableField) return;

		if (editMode === "row")
			setRowModesModel((prev) => ({
				...prev,
				[rowId]: {
					mode: GridRowModes.Edit,
					fieldToFocus: firstEditableField,
				},
			}));
		else if (editMode === "cell")
			gridApi.startCellEditMode({
				id: rowId,
				field: firstEditableField,
			});

		handleEditStarted(rowId);
	}, [editMode, getGridApi]);

	React.useEffect(() => {
		if (rowToStartEdit === undefined) return;
		if (!isNotNullOrEmptyObject(gridApiRef.current)) return;
		if (editMode === "cell" && getGridApi().getRow(rowToStartEdit) == null) return;

		startToEditNewRow(rowToStartEdit);
		setRowToStartEdit(undefined);
	}, [editMode, getGridApi, gridApiRef, rows, rowToStartEdit, startToEditNewRow]);

	function handleEditStarted(rowId: GridRowId) {
		setRowUnderEdit(rowId);
	}

	return {
		rowUnderEdit,
		rowModesModel,
		setRowModesModel,
		requestStartNewRowEdit,
		handleEditStarted,
		stopEdit,
		resetEditing,
		clearDeletedRows,
	};
}
